/**
 * IncidentMarker
 *
 * Map marker component for displaying a VMS incident on the dispatch map.
 * Color-coded based on severity, with pulsing animation for new/reviewing incidents.
 * Popup shows incident details with action buttons.
 *
 * Popup state is controlled by parent to ensure only one popup is open at a time.
 */

import React, { memo, useState, useCallback } from 'react';
import { Box, Paper, Typography, Button, Chip, ClickAwayListener } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import styles from './IncidentMarker.module.css';
import classNames from 'classnames';
import { updateIncidentStatus } from '../../../../../api/canvms/incidents';
import type { VmsIncidentMapData } from '../../../../CANvision/types/canvision.types';

/**
 * Severity color mapping
 * NOTE: These are used as inline styles for google-map-react markers which
 * render outside the MUI ThemeProvider context, so resolved hex values are required.
 * The values correspond to MUI default palette tokens:
 *   critical -> error.dark
 *   high     -> warning.dark
 *   medium   -> warning.light
 *   low      -> primary.main
 */
export const INCIDENT_SEVERITY_COLORS: Record<string, string> = {
  critical: '#D32F2F',
  high: '#E67E22',
  medium: '#F9A825',
  low: '#1976D2',
};

/** Severity labels */
const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

interface IncidentMarkerProps {
  /** Incident data */
  incident: VmsIncidentMapData;
  /** Whether the popup should be shown (controlled by parent) */
  showPopup?: boolean;
  /** Callback when marker is clicked */
  onClick?: (incident: VmsIncidentMapData) => void;
  /** Callback when "View Details" is clicked */
  onViewDetails?: (incident: VmsIncidentMapData) => void;
  /** Callback when "Dispatch Guard" is clicked */
  onDispatch?: (incident: VmsIncidentMapData) => void;
  /** Callback to close the popup */
  onClosePopup?: () => void;
  /** Callback after quick resolve completes */
  onResolved?: (incidentId: number) => void;
  /** Required by google-map-react for positioning */
  lat: number;
  /** Required by google-map-react for positioning */
  lng: number;
}

/** Format relative time (e.g. "5 min ago", "2 hrs ago") */
function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs} hr${diffHrs !== 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
}

/** Build the thumbnail URL for an incident */
const getThumbnailUrl = (incidentId: number): string => {
  const baseURL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:10000'
    : '';
  return `${baseURL}/api3.2/canvms/incidents/${incidentId}/thumbnail`;
};

const IncidentMarker: React.FC<IncidentMarkerProps> = ({
  incident,
  showPopup = false,
  onClick,
  onViewDetails,
  onDispatch,
  onClosePopup,
  onResolved,
}) => {
  const [resolving, setResolving] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);

  const severityColor = INCIDENT_SEVERITY_COLORS[incident.severity] || INCIDENT_SEVERITY_COLORS.medium;
  const shouldPulse = incident.status === 'new' || incident.status === 'reviewing';
  const displayName = incident.camera_display_name || incident.camera_name || incident.site_display_name || incident.site_name;

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(incident);
  }, [onClick, incident]);

  const handleViewDetails = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onViewDetails?.(incident);
  }, [onViewDetails, incident]);

  const handleDispatch = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDispatch?.(incident);
  }, [onDispatch, incident]);

  const handleQuickResolve = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (resolving) return;
    setResolving(true);
    try {
      await updateIncidentStatus(incident.id, 'resolved', undefined, 'false_alarm');
      onResolved?.(incident.id);
    } catch (err) {
      console.error('[IncidentMarker] Quick resolve failed:', err);
    } finally {
      setResolving(false);
    }
  }, [incident.id, resolving, onResolved]);

  const handleClickAway = useCallback(() => {
    if (showPopup) {
      onClosePopup?.();
    }
  }, [showPopup, onClosePopup]);

  const markerContent = (
    <div
      className={classNames(styles.markerContainer, {
        [styles.selected]: showPopup,
      })}
    >
      {/* Marker Pin */}
      <div
        className={classNames(styles.markerPin, {
          [styles.pulsing]: shouldPulse,
          [styles.severityCritical]: incident.severity === 'critical',
          [styles.severityHigh]: incident.severity === 'high',
          [styles.selected]: showPopup,
        })}
        style={{ backgroundColor: severityColor, color: severityColor }}
        onClick={handleClick}
        title={`${incident.title} — ${SEVERITY_LABELS[incident.severity] || incident.severity}`}
      >
        <WarningAmberIcon className={styles.markerIcon} />

        {/* Severity Badge */}
        <div
          className={styles.severityBadge}
          style={{ backgroundColor: severityColor }}
        >
          {incident.severity.charAt(0).toUpperCase()}
        </div>
      </div>

      {/* Popup */}
      {showPopup && (
        <Paper className={styles.popup} elevation={4}>
          {/* Header */}
          <Box className={styles.popupHeader}>
            <Box
              className={styles.popupSeverityDot}
              style={{ backgroundColor: severityColor }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography className={styles.popupTitle}>
                {incident.title}
              </Typography>
              <Typography className={styles.timeAgo}>
                {formatTimeAgo(incident.created_at)}
              </Typography>
            </Box>
          </Box>

          {/* Thumbnail */}
          {incident.thumbnail_url && !thumbnailError && (
            <img
              className={styles.thumbnail}
              src={getThumbnailUrl(incident.id)}
              alt="Incident thumbnail"
              onError={() => setThumbnailError(true)}
            />
          )}

          {/* Details rows */}
          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">Type:</Typography>
            <Typography variant="body2" className={styles.popupValue}>
              {incident.incident_type.replace(/_/g, ' ')}
            </Typography>
          </Box>

          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">Severity:</Typography>
            <Chip
              label={SEVERITY_LABELS[incident.severity] || incident.severity}
              size="small"
              sx={{
                backgroundColor: severityColor,
                color: 'common.white',
                fontWeight: 600,
                height: 22,
                fontSize: '0.75rem',
              }}
            />
          </Box>

          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">Location:</Typography>
            <Typography variant="body2" className={styles.popupValue}>
              {displayName}
            </Typography>
          </Box>

          {incident.patrol_id && (
            <Box className={styles.popupRow}>
              <Typography variant="body2" color="textSecondary">Status:</Typography>
              <Chip
                label="Dispatched"
                size="small"
                color="info"
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
            </Box>
          )}

          {/* Actions */}
          <Box className={styles.popupActions}>
            <Button
              variant="contained"
              size="small"
              onClick={handleViewDetails}
              sx={{ flex: 1, fontSize: '0.75rem' }}
            >
              View Details
            </Button>
            {!incident.patrol_id && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleQuickResolve}
                disabled={resolving}
                sx={{ flex: 1, fontSize: '0.75rem' }}
              >
                {resolving ? 'Resolving...' : 'Quick Resolve'}
              </Button>
            )}
            {!incident.patrol_id && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={handleDispatch}
                sx={{ flex: 1, fontSize: '0.75rem' }}
              >
                Dispatch
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </div>
  );

  // Wrap with ClickAwayListener only when popup is shown
  if (showPopup) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        {markerContent}
      </ClickAwayListener>
    );
  }

  return markerContent;
};

/**
 * Custom comparison for React.memo
 */
function arePropsEqual(
  prevProps: IncidentMarkerProps,
  nextProps: IncidentMarkerProps
): boolean {
  if (prevProps.showPopup !== nextProps.showPopup) return false;

  const prev = prevProps.incident;
  const next = nextProps.incident;

  if (
    prev.id !== next.id ||
    prev.severity !== next.severity ||
    prev.status !== next.status ||
    prev.title !== next.title ||
    prev.latitude !== next.latitude ||
    prev.longitude !== next.longitude ||
    prev.patrol_id !== next.patrol_id
  ) {
    return false;
  }

  return true;
}

export default memo(IncidentMarker, arePropsEqual);
